import { Stack, useMantineTheme } from '@mantine/core';
import { runInAction, set } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { CustomField, IImportColumn, Importer } from '@feathr/blackbox';
import { FieldCollection, FieldDataType } from '@feathr/blackbox';
import { ConfirmModal, Input } from '@feathr/components';
import DataTypeSelect from '@feathr/extender/components/DataTypeSelect';
import { useStore } from '@feathr/extender/state';

interface IProps {
  importer: Importer;
  /** The mapping row the new field is mapped to. */
  column: IImportColumn;
  /** The column's existing new field, to edit it rather than create another. */
  customField?: CustomField;
  opened: boolean;
  onClose: () => void;
}

function clean(name: string): string {
  return name.toLowerCase().trim();
}

/**
 * Creates a custom field for one column of an import and maps the column to it. The field stays
 * ephemeral: the import saves it when it starts, so an abandoned draft leaves no field behind.
 */
function CreateImportFieldModal({
  importer,
  column,
  customField,
  opened,
  onClose,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { CustomFields } = useStore();
  const theme = useMantineTheme();
  const [name, setName] = useState(customField?.get('u_key') ?? '');
  const [dataType, setDataType] = useState<FieldDataType | undefined>(
    customField?.get('data_type') ?? FieldDataType.str,
  );
  const [nameErrors, setNameErrors] = useState<string[]>([]);
  // The name the errors belong to: checking is async, so confirm waits until it catches up.
  const [validatedName, setValidatedName] = useState<string | undefined>(undefined);

  // Re-runs only on the user's own edits; validation sets errors, never the name.
  useEffect(() => {
    let isCurrent = true;

    async function validate(): Promise<void> {
      if (!name.trim()) {
        setNameErrors([t('Field name is required.')]);
        setValidatedName(name);
        return;
      }
      const otherNewFields = importer
        .getEphemeralCustomFields(CustomFields)
        .filter((field) => field !== customField);
      const isDuplicateInImport = otherNewFields.some(
        (field) => clean(field.get('u_key', '')) === clean(name),
      );
      const uniquenessError = isDuplicateInImport
        ? t('Another column in this import already creates a field with this name.')
        : await CustomFields.validateUniqueness({
            newField: { collection: FieldCollection.Person, u_key: name },
            collection: FieldCollection.Person,
            t,
          });
      if (isCurrent) {
        setNameErrors(uniquenessError ? [uniquenessError] : []);
        setValidatedName(name);
      }
    }

    void validate();
    return (): void => {
      isCurrent = false;
    };
  }, [name]);

  function handleChangeName(newValue?: string): void {
    setName(newValue ?? '');
  }

  function handleChangeDataType(newValue?: FieldDataType): void {
    setDataType(newValue);
  }

  function handleConfirm(): void {
    const fieldName = name.trim();
    const field =
      customField ??
      CustomFields.create({ collection: FieldCollection.Person, data_type: dataType, u_key: fieldName });
    runInAction(() => {
      field.set({ data_type: dataType, u_key: fieldName });
      set(column, { attr_type: dataType, feathr_attr: field.id, u_key: fieldName });
    });
    importer.setAttributeDirty('column_mappers');
    onClose();
  }

  const errors = dataType ? nameErrors : [...nameErrors, t('Data type is required.')];

  return (
    <ConfirmModal
      allowOverflow={true}
      confirmButtonText={customField ? t('Save field') : t('Create field')}
      confirmDisabled={errors.length > 0 || validatedName !== name}
      description={t('This adds a new field to Feathr and maps it to this column.')}
      onClose={onClose}
      onConfirm={handleConfirm}
      opened={opened}
      t={t}
      title={customField ? t('Edit custom Feathr field') : t('Create a custom Feathr field')}
    >
      <Stack gap={theme.spacing['4']}>
        <Input
          label={t('Field name')}
          name={'new-field-name'}
          onChange={handleChangeName}
          placeholder={t('e.g. Preferred pronoun')}
          type={'text'}
          validationError={name ? nameErrors : undefined}
          value={name}
        />
        <DataTypeSelect label={t('Data type')} onChange={handleChangeDataType} value={dataType} />
      </Stack>
    </ConfirmModal>
  );
}

export default observer(CreateImportFieldModal);
